var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useUpdateEffect } from '../../hooks';
import { endOfDay, startOfMonth, isSameDay, subDays, startOfDay } from 'date-fns';
import { getActiveRange } from '../../services';
import styles from './DateRange.module.scss';
import { TimePicker, RangePicker, CustomRanges } from './components';
import { Button } from '../Button/Button';
var DateRange = function (_a) {
    var _b;
    var months = _a.months, start = _a.start, end = _a.end, time = _a.time, customRanges = _a.customRanges, compareable = _a.compareable, className = _a.className, minDate = _a.minDate, maxDate = _a.maxDate, id = _a.id, dataId = _a.dataId, onChange = _a.onChange, onCancel = _a.onCancel;
    var defaultFocus = startOfMonth(new Date());
    var defaultStart = subDays(startOfDay(new Date()), 6);
    var defaultEnd = endOfDay(new Date());
    var _c = useState(defaultFocus), focusDate = _c[0], setFocusDate = _c[1];
    var _d = useState(start || defaultStart), startDate = _d[0], setStartDate = _d[1];
    var _e = useState(end || defaultEnd), endDate = _e[0], setEndDate = _e[1];
    var _f = useState(defaultStart), startCompareDate = _f[0], setStartCompareDate = _f[1];
    var _g = useState(defaultEnd), endCompareDate = _g[0], setEndCompareDate = _g[1];
    var _h = useState((customRanges && ((_b = getActiveRange(startDate, endDate)) === null || _b === void 0 ? void 0 : _b.label)) || ''), customRange = _h[0], setCustomRange = _h[1];
    var _j = useState(false), compareOn = _j[0], setCompareOn = _j[1];
    var onDateSubmit = function () {
        onChange({ startDate: startDate, endDate: endDate, customRange: customRange });
    };
    var setWithCompare = function (start, end) {
        !isSameDay(start, startCompareDate) && setStartCompareDate(start);
        !isSameDay(endOfDay(end), endCompareDate) && setEndCompareDate(endOfDay(end));
    };
    var setWithoutCompare = function (start, end) {
        if (!isSameDay(start, startDate)) {
            setStartDate(start);
            setStartCompareDate(start);
        }
        if (!isSameDay(endOfDay(end), endDate)) {
            setEndDate(endOfDay(end));
            setEndCompareDate(endOfDay(end));
        }
    };
    var onRangeChange = function (focus, start, end) {
        var activeRange = getActiveRange(start, end);
        var setFn = compareOn ? setWithCompare : setWithoutCompare;
        !isSameDay(focus, focusDate) && setFocusDate(focus);
        setFn(start, end);
        customRanges && setCustomRange((activeRange === null || activeRange === void 0 ? void 0 : activeRange.label) || '');
    };
    useUpdateEffect(function () {
        start && setStartDate(start);
        end && setEndDate(end);
    }, [start, end]);
    useUpdateEffect(function () {
        if (!compareOn) {
            setStartCompareDate(startDate);
            setEndCompareDate(endDate);
        }
    }, [compareOn]);
    var onCompareChange = function () {
        setCompareOn(function (prev) { return !prev; });
        setFocusDate(defaultFocus);
    };
    var onClear = function () {
        var _a;
        setFocusDate(defaultFocus);
        setCompareOn(false);
        setStartDate(defaultStart);
        setStartCompareDate(defaultStart);
        setEndDate(defaultEnd);
        setEndCompareDate(defaultEnd);
        customRanges && setCustomRange((_a = getActiveRange(defaultStart, defaultEnd)) === null || _a === void 0 ? void 0 : _a.label);
    };
    var renderTimePicker = function () { return (_jsx(TimePicker, { startDate: startDate, endDate: endDate, onStartChange: setStartDate, onEndChange: setEndDate })); };
    var renderCompareTimePicker = function () { return (_jsx(TimePicker, { startDate: startCompareDate, endDate: endCompareDate, onStartChange: setStartCompareDate, onEndChange: setEndCompareDate })); };
    return (_jsx("section", __assign({ className: "".concat(styles.DateRange, " ").concat(className ? className : ''), id: id, "data-testid": dataId }, { children: _jsxs("section", __assign({ className: styles.RangeWrapper }, { children: [customRanges && (_jsx(CustomRanges, { startDate: startDate, endDate: endDate, compareOn: compareOn, onChange: onRangeChange })), _jsxs("section", { children: [_jsx(RangePicker, { focusDate: focusDate, startDate: startDate, endDate: endDate, startCompareDate: startCompareDate, endCompareDate: endCompareDate, customRange: customRange, months: months, minDate: minDate, maxDate: maxDate, compareable: compareable, compareOn: compareOn, onCompareChange: onCompareChange, onChange: onRangeChange }), time && renderTimePicker(), time && compareOn && renderCompareTimePicker(), _jsxs("footer", __assign({ className: styles.Actions, onClick: function (e) { return e.stopPropagation(); } }, { children: [_jsx("span", __assign({ className: styles.Clear, onClick: onClear }, { children: "Clear Values" })), _jsxs("section", { children: [_jsx(Button, { small: true, title: 'Discard', onClick: onCancel }), _jsx(Button, { small: true, primary: true, title: 'Confirm', className: styles.Submit, onClick: onDateSubmit })] })] }))] })] })) })));
};
DateRange.propTypes = {
    months: PropTypes.number,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    time: PropTypes.bool,
    customRanges: PropTypes.bool,
    compareable: PropTypes.bool,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    className: PropTypes.string,
    id: PropTypes.string,
    dataId: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
export { DateRange };
//# sourceMappingURL=DateRange.js.map